import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors, closestCorners, DragOverlay } from '@dnd-kit/core';
import { crmApi } from '../../api/crm.js';
import { useRealtime } from '../../hooks/useRealtime.js';

export default function PipelineBoard() {
  const [pipelines, setPipelines] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [board, setBoard] = useState(null);
  const [activeDeal, setActiveDeal] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    crmApi.pipelines().then((d) => {
      setPipelines(d?.items || []);
      const def = (d?.items || []).find((p) => p.is_default) || (d?.items || [])[0];
      if (def) setActiveId(def.id);
    });
  }, []);

  const reload = useCallback(() => {
    if (activeId) crmApi.pipelineBoard(activeId).then(setBoard);
  }, [activeId]);

  useEffect(reload, [reload]);

  // Live updates: when anyone moves a deal in this pipeline, refresh.
  useRealtime({
    type: 'pipeline',
    id: activeId,
    events: { 'deal:stage:changed': () => reload() },
  });

  const allDeals = useMemo(() => board?.stages?.flatMap((s) => s.deals) || [], [board]);
  const dragging = activeDeal ? allDeals.find((d) => d.id === activeDeal) : null;

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  async function onDragEnd(e) {
    setActiveDeal(null);
    if (!e.over) return;
    const dealId = e.active.id;
    const overId = e.over.id;
    const targetStage = board.stages.find((s) =>
      `stage-${s.name}` === overId || s.deals.some((d) => d.id === overId)
    );
    if (!targetStage) return;
    const moved = allDeals.find((d) => d.id === dealId);
    if (!moved || moved.stage === targetStage.name) return;

    // Optimistic move.
    setBoard((prev) => moveDeal(prev, dealId, targetStage.name));
    try {
      const status = targetStage.name.toLowerCase() === 'won' ? 'won' : moved.status;
      await crmApi.changeStage(dealId, { stage: targetStage.name, status });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update stage');
      reload();
    }
  }

  if (!pipelines.length) return <div className="muted">No pipelines configured.</div>;
  if (!board) return <div className="muted">Loading…</div>;

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>Pipeline Board</h1>
          <select value={activeId || ''} onChange={(e) => setActiveId(e.target.value)}>
            {pipelines.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <Link to="/crm/deals/new" className="btn primary">+ New Deal</Link>
      </header>
      {error && <div className="alert error">{error}</div>}

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={(e) => setActiveDeal(e.active.id)} onDragEnd={onDragEnd}>
        <div className="kanban">
          {board.stages.map((stage) => (
            <Stage key={stage.name} stage={stage} />
          ))}
        </div>
        <DragOverlay>{dragging && <DealCard deal={dragging} dragging />}</DragOverlay>
      </DndContext>
    </section>
  );
}

function moveDeal(board, dealId, targetStageName) {
  const deal = board.stages.flatMap((s) => s.deals).find((d) => d.id === dealId);
  if (!deal) return board;
  return {
    ...board,
    stages: board.stages.map((s) => {
      if (s.name === targetStageName) {
        const next = [...s.deals.filter((d) => d.id !== dealId), { ...deal, stage: targetStageName }];
        return { ...s, deals: next, total: next.reduce((sum, d) => sum + Number(d.value || 0), 0) };
      }
      const next = s.deals.filter((d) => d.id !== dealId);
      return { ...s, deals: next, total: next.reduce((sum, d) => sum + Number(d.value || 0), 0) };
    }),
  };
}

function Stage({ stage }) {
  const { setNodeRef, isOver } = useDroppable({ id: `stage-${stage.name}` });
  return (
    <div ref={setNodeRef} className={`kcol ${isOver ? 'kcol-over' : ''}`}>
      <header>
        <span className="kcol-name">{stage.name}</span>
        <span className="kcol-count">{stage.deals.length}</span>
      </header>
      <div className="muted small" style={{ padding: '0 6px 6px' }}>
        {stage.total ? Number(stage.total).toLocaleString(undefined, { maximumFractionDigits: 0 }) : 0} ·
        {' '}wtd {Number(stage.weighted || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} ({stage.probability}%)
      </div>
      <div className="kcol-body">
        {stage.deals.map((d) => <DraggableDeal key={d.id} deal={d} />)}
        {stage.deals.length === 0 && <div className="kcol-empty muted small">Empty</div>}
      </div>
    </div>
  );
}

function DraggableDeal({ deal }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: deal.id });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` } : {};
  return (
    <div ref={setNodeRef} style={{ ...style, opacity: isDragging ? 0.4 : 1 }} {...attributes} {...listeners}>
      <DealCard deal={deal} />
    </div>
  );
}

function DealCard({ deal, dragging }) {
  return (
    <Link
      to={`/crm/deals/${deal.id}`}
      className={`issue-card prio-${deal.status} ${dragging ? 'dragging' : ''}`}
      onClick={(e) => dragging && e.preventDefault()}
    >
      <div className="issue-title">{deal.name}</div>
      <div className="muted small">{deal.company_name || deal.contact_name || 'No customer'}</div>
      <footer>
        <strong>{deal.currency} {Number(deal.value).toLocaleString()}</strong>
        {deal.expected_close && <span className="muted small">due {deal.expected_close}</span>}
      </footer>
    </Link>
  );
}
